import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../../components/header/header.component';
import { FormSectionComponent } from '../../components/form-section/form-section.component';
import { TextInputComponent } from '../../components/text-input/text-input.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { RadioComponent } from '../../components/radio/radio.component';
import { FileUploaderComponent } from '../../components/file-uploader/file-uploader.component';
import { ButtonComponent } from '../../components/button/button.component';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { UniversityService } from '../../services/university.service';
import { ProjectSettingsService } from '../../services/project-settings.service';
import { ValidationService } from '../../services/validation.service';

@Component({
  selector: 'app-university-form-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    FormSectionComponent,
    TextInputComponent,
    SelectInputComponent,
    RadioComponent,
    FileUploaderComponent,
    ButtonComponent,
  ],
  templateUrl: './university-form-page.component.html',
})
export class UniversityFormPageComponent {
  @ViewChild('logoUploader') logoUploader: FileUploaderComponent | undefined;

  form: FormGroup;
  params: any = {};
  isSubmitting = false;

  enable_disable_options = [
    { label: 'Enable', value: 'enable' },
    { label: 'Disable', value: 'disable' },
  ];

  assessment_type_options = [
    { label: 'Auto', value: 'auto' },
    { label: 'Manual', value: 'manual' },
    { label: 'Both', value: 'both' },
  ];

  constructor(
    private fb: FormBuilder,
    public gs: GlobalService,
    public ds: DropdownService,
    public us: UniversityService,
    public ps: ProjectSettingsService,
    public vs: ValidationService,
    private ar: ActivatedRoute,
    private router: Router,
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(this.vs.min_length_name), Validators.maxLength(this.vs.max_length_name), Validators.pattern(this.vs.name_pattern)]],
      code: ['', [Validators.required, Validators.minLength(this.vs.min_length_code), Validators.maxLength(this.vs.max_length_code), Validators.pattern(this.vs.code_pattern)]],
      city_id: ['', [Validators.required]],
      address: ['', [Validators.required, Validators.minLength(this.vs.min_length_address)]],
      email: ['', [Validators.required, Validators.maxLength(this.vs.max_length_email), Validators.pattern(this.vs.email_pattern)]],
      contact: ['', [Validators.required, Validators.minLength(this.vs.min_length_contact), Validators.maxLength(this.vs.max_length_contact), Validators.pattern(this.vs.contact_pattern)]],
      face_verification: ['disable'],
      aadhaar_verification: ['disable'],
      image_to_text: ['disable'],
      ai_proctoring: ['disable'],
      ai_evaluation: ['disable'],
      assessment_type: ['manual'],
      logo_url: [''],
      receipt_university_name: ['', [Validators.maxLength(200)]],
      receipt_address: [''],
      gst_required: ['no'],
      gstn: ['',],
      signatory_name: [''],
      designation: [''],
      signature_url: [''],
      admin_config_required: [false],
    });

    this.form.get('gst_required')?.valueChanges.subscribe((val: string) => {
      const gstn_ctrl = this.form.get('gstn');
      if (val === 'yes') {
        gstn_ctrl?.setValidators([Validators.required, Validators.pattern(this.vs.gst_pattern)]);
      } else {
        gstn_ctrl?.clearValidators();
        gstn_ctrl?.setValue('');
      }
      gstn_ctrl?.updateValueAndValidity();
    });

    this.form.get('admin_config_required')?.valueChanges.subscribe((required: boolean) => {
      this.apply_admin_config_validators(required);
    });

    // Receipt name mirrors the main University Name as the admin types it, but stops following
    // the moment they type something different into the receipt field themselves — comparing
    // against the last-seen name value (rather than a separate "touched" flag) means it resumes
    // following automatically if they clear the override back out.
    let last_name_value = '';
    this.form.get('name')?.valueChanges.subscribe((val: string) => {
      const receipt_ctrl = this.form.get('receipt_university_name');
      const current_receipt_value = receipt_ctrl?.value || '';
      if (!current_receipt_value || current_receipt_value === last_name_value) {
        receipt_ctrl?.setValue(val, { emitEvent: false });
      }
      last_name_value = val;
    });
  }

  admin_config_field_names = ['logo_url', 'receipt_university_name', 'receipt_address', 'signatory_name', 'designation', 'signature_url'];

  apply_admin_config_validators(required: boolean) {
    this.admin_config_field_names.forEach((field) => {
      const ctrl = this.form.get(field);
      if (required) {
        const validators = field === 'receipt_address'
          ? [Validators.required, Validators.minLength(this.vs.min_length_address), Validators.maxLength(this.vs.max_length_address)]
          : field === 'receipt_university_name'
            ? [Validators.required, Validators.maxLength(200)]
            : [Validators.required];
        ctrl?.setValidators(validators);
      } else {
        ctrl?.clearValidators();
      }
      ctrl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  get admin_config_required(): boolean {
    return !!this.form.get('admin_config_required')?.value;
  }

  async ngOnInit() {
    await this.ds.get_dropdown_list(['city']);
    this.ar.queryParams.subscribe(async (params: any) => {
      this.params = { ...params };
      if (params.id) {
        await this.load_university(params.id);
      }
    });
  }

  async load_university(id: any) {
    try {
      const res = await this.us.get(id);
      if (res?.data) {
        this.form.patchValue(res.data);
      }
      const config_res = await this.ps.get_config(id);
      if (config_res?.data) {
        const c = config_res.data;
        this.form.patchValue({
          logo_url: c.logo_url || '',
          // Fall back to the main University Name when no receipt-specific name was ever saved
          // (e.g. Admin Configuration being enabled for the first time on an existing university).
          receipt_university_name: c.receipt_university_name || this.form.get('name')?.value || '',
          receipt_address: c.address || '',
          gst_required: c.gst_enabled ? 'yes' : 'no',
          gstn: c.gstn || '',
          signatory_name: c.signatory_name || '',
          designation: c.designation || '',
          signature_url: c.signature_url || '',
          admin_config_required: !!c.admin_config_required,
        });
      }
    } catch { }
  }

  on_logo_uploaded(event: any) {
    const url = event?.url || event?.file_path || event?.data?.url;
    if (url) {
      const full_url = url.startsWith('http') ? url : `${this.ps.hs.base_url}${url}`;
      this.form.patchValue({ logo_url: full_url });
    }
  }

  on_signature_uploaded(event: any) {
    const url = event?.url || event?.file_path || event?.data?.url;
    if (url) {
      const full_url = url.startsWith('http') ? url : `${this.ps.hs.base_url}${url}`;
      this.form.patchValue({ signature_url: full_url });
    }
  }

  get gst_required(): string {
    return this.form.get('gst_required')?.value || 'no';
  }

  async submit() {
    if (this.isSubmitting) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
      return;
    }
    try {
      this.isSubmitting = true;
      const v = this.form.getRawValue();

      const university_payload: any = {
        code: v.code,
        name: v.name,
        city_id: v.city_id,
        address: v.address,
        contact: v.contact,
        email: v.email,
        face_verification: v.face_verification,
        aadhaar_verification: v.aadhaar_verification,
        image_to_text: v.image_to_text,
        ai_proctoring: v.ai_proctoring,
        ai_evaluation: v.ai_evaluation,
        assessment_type: v.assessment_type,
        is_active: true,
      };

      let university_id: any;
      if (this.params.id) {
        await this.us.update(this.params.id, university_payload);
        university_id = this.params.id;
      } else {
        const res = await this.us.add(university_payload);
        university_id = res?.data?.id || res?.id;
      }

      if (university_id) {
        const config_payload: any = {
          university_id: Number(university_id),
          logo_url: v.logo_url,
          receipt_university_name: v.receipt_university_name,
          address: v.receipt_address,
          gst_enabled: v.gst_required === 'yes',
          signatory_name: v.signatory_name,
          designation: v.designation,
          signature_url: v.signature_url,
          admin_config_required: v.admin_config_required,
        };
        if (v.gst_required === 'yes') config_payload.gstn = v.gstn;
        await this.ps.save_config(config_payload);
      }

      this.gs.toastr_shows_function(
        this.params.id ? 'University updated successfully.' : 'University added successfully.',
        'Success',
        'success',
      );
      this.router.navigate(['/university/list']);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message || 'Failed to save.', 'Error', 'error');
    } finally {
      this.isSubmitting = false;
    }
  }
}
